import { IonGrid, IonItem, IonText } from '@ionic/react';
import { FormikErrors, FormikHelpers, useFormikContext } from 'formik';
import { debounce, isNil } from 'lodash';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDeepCompareEffect, usePrevious } from 'react-use';
import {
  PlanEntitlementKey,
  useGetScratchpadRecommendationLazyQuery,
} from '../../../../../../api/graphql/generated';
import { IntercomHelp } from '../../../../../../components/help/intercom-help';
import { PaywallWrapper } from '../../../../../../components/paywall/paywall-wrapper';
import { SaveStatusToast } from '../../../../../../components/toast/toast.gql';
import { trackScratchpadRecommendationCreated } from '../../../../../../config/analytics';
import { IntercomArticle } from '../../../../../../config/intercom';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { useIsMounted } from '../../../../../../util/hooks';
import { logger } from '../../../../../../util/logger';
import {
  ParametersList,
  ParametersRecommendationInfo,
} from '../../../../shared/component/flow-item-parameter/parameter-list';
import { ApplicationDefaultParameterSetLocalFormikFormValues } from '../../../../shared/component/settings/application-settings-edit';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import { useScratchpadRecommendationAccess } from '../../../../shared/hook/recommendation.hooks';
import {
  Flow,
  FlowItem,
  FlowItemForm,
  FlowItemParameterMetadata,
} from '../../../../shared/model/flow.types';
import { getParameterValue } from '../../../../shared/util/flow-parameter.util';
import { BeaconCuttingData } from '../../model/beacon-cutting-data';
import { getCuttingParameterMetadataFromForm } from '../../util/beacon-cutting.util';
import {
  CutParameterRecommendation,
  CutSettingsRecommendation,
  SingleCutRecommendation,
  createParamSubtitlesForSettingsRecommendations,
  setFormParamValues,
  useCreateCutSettingsRecommendationForForm,
} from '../settings/recommendations/settings-recommendation.hook';
import {
  convertFormParametersToNumbersFromString,
  findFormDifferences,
} from '../settings/recommendations/settings-recommendation.utils';
import { CutProcessSettingsFormProps } from './cut-process-settings-form';

interface AssistedCutProcessSettingsFormProps
  extends CutProcessSettingsFormProps {
  onRecommendSettings?: (recommendationId: string | undefined) => void;
  onRecommendSettingsLoading?: (loading: boolean) => void;
  preventRecommendation?: boolean;
}

const AssistedCutProcessSettingsForm = (
  props: AssistedCutProcessSettingsFormProps
): ReactElement => {
  const {
    extensions,
    system,
    setValidationSchema,
    parameterListPrefix,
    parameterOptions,
    onRecommendSettings,
    onRecommendSettingsLoading,
    preventRecommendation,
    readOnly,
  } = props;

  const { flowItemService, flow } = useFlowServices();
  const formik =
    useFormikContext<ApplicationDefaultParameterSetLocalFormikFormValues>();
  const [params, setParams] = useState<FlowItemParameterMetadata[]>([]);
  const [previousForm, setPreviousForm] =
    useState<ApplicationDefaultParameterSetLocalFormikFormValues>(
      formik.values
    );
  const [recommendation, setRecommendation] =
    useState<SingleCutRecommendation>();
  const [isLoadingRecommendation, setLoadingRecommendation] = useState(false);
  const [recommendationFailed, setRecommendationFailed] = useState(false);
  const [parametersRecommendationInfo, setParametersRecommendationInfo] =
    useState<
      | ParametersRecommendationInfo<BeaconCuttingData.FlowItemParameterRef>
      | undefined
    >();
  const getCutRecommendationForForm =
    useCreateCutSettingsRecommendationForForm();
  const isMounted = useIsMounted();
  const { isEnabled: isScratchpadRecommendationEnabled } =
    useScratchpadRecommendationAccess();

  const [getScratchpadRecommendation] =
    useGetScratchpadRecommendationLazyQuery();

  const updateLocalRecommendation = useCallback(
    async (flow: Flow) => {
      if (flow !== undefined && flow.recommendation !== undefined) {
        const scratchpadRecommendation = await getScratchpadRecommendation({
          variables: {
            input: {
              id: flow.recommendation.id as string,
            },
          },
          fetchPolicy: 'cache-first',
        });

        const existingRecommendations =
          scratchpadRecommendation.data?.getScratchpadRecommendation;

        if (existingRecommendations) {
          const existingRecommendation =
            // NOTE weld recommendations are arrays
            (scratchpadRecommendation.data?.getScratchpadRecommendation?.outputs
              .recommendation as CutSettingsRecommendation) ?? undefined;

          if (existingRecommendation) {
            const subtitles = createParamSubtitlesForSettingsRecommendations(
              existingRecommendation.parameters
            );

            setRecommendation({
              ...existingRecommendations,
              recommendation: existingRecommendation,
            });
            setParametersRecommendationInfo(subtitles);
          }
        }
      }
    },
    [getScratchpadRecommendation]
  );

  const previousRecommendation = usePrevious(flow.recommendation?.id);

  const updateLocalRecommendationDebounced = debounce(
    updateLocalRecommendation
  );

  useEffect(() => {
    const mounted = isMounted();
    if (
      !mounted ||
      !isScratchpadRecommendationEnabled ||
      previousRecommendation === flow.recommendation?.id
    ) {
      return;
    }

    updateLocalRecommendationDebounced(flow);

    return () => {
      updateLocalRecommendationDebounced.cancel();
    };
  }, [
    flow,
    isScratchpadRecommendationEnabled,
    isMounted,
    previousRecommendation,
    updateLocalRecommendationDebounced,
  ]);

  useEffect(() => {
    if (onRecommendSettingsLoading) {
      onRecommendSettingsLoading(isLoadingRecommendation);
    }
  }, [isLoadingRecommendation, onRecommendSettingsLoading]);

  const [parameterFormOptions, setParameterFormOptions] =
    useState<BeaconCuttingData.GetParameterMetadataFromFormOptions>();

  useDeepCompareEffect(() => {
    setParameterFormOptions({
      ...parameterOptions,
    });
  }, [parameterOptions ?? {}]);

  const updateSchema = useCallback(
    (form: ApplicationDefaultParameterSetLocalFormikFormValues) => {
      const actualForm: FlowItemForm = form.data;

      const params = getCuttingParameterMetadataFromForm(
        flowItemService,
        actualForm.parameters,
        parameterFormOptions
      );

      const filteredParams: FlowItemParameterMetadata[] = [];

      for (const ref of BeaconCuttingData.RECOMMENDED_PARAM_OUTPUTS) {
        const foundParam = params.find((p) => p.ref === ref);

        if (foundParam) {
          // we clone so the shared param object isn't modified
          filteredParams.push(foundParam);
        }
      }

      const schema = flowItemService.getYupSchemaForItem(
        BeaconCuttingData.FlowItemRef.CUT,
        props.system,
        { flow, form: actualForm },
        filteredParams
      );

      setParams(filteredParams);
      setValidationSchema(schema);
    },
    [
      flowItemService,
      parameterFormOptions,
      props.system,
      setValidationSchema,
      flow,
    ]
  );

  const recommendationInFlight = useRef(false);
  const hasInitialRecommendation = useRef(flow.recommendation !== undefined);
  const [noRecommendationsExist, setNoRecommendationsExist] = useState(false);

  useEffect(() => {
    if (
      !hasInitialRecommendation.current &&
      flow.recommendation !== undefined
    ) {
      hasInitialRecommendation.current = true;
    }
  }, [flow.recommendation]);

  const recommendParameters = useCallback(
    async (
      currentForm: ApplicationDefaultParameterSetLocalFormikFormValues,
      previousForm: ApplicationDefaultParameterSetLocalFormikFormValues,
      setFieldValue: FormikHelpers<FlowItemForm>['setFieldValue'],
      validateForm: FormikHelpers<ApplicationDefaultParameterSetLocalFormikFormValues>['validateForm']
    ) => {
      if (recommendationInFlight.current) {
        return;
      }

      recommendationInFlight.current = true;
      const formErrors = await validateForm(currentForm);
      const formHasValidErrors = containsValidErrors(formErrors);

      if (formHasValidErrors) {
        recommendationInFlight.current = false;
        return;
      }

      setPreviousForm(currentForm);

      const convertedPreviousFormParams =
        convertFormParametersToNumbersFromString(
          previousForm.data,
          flowItemService
        );

      const convertedCurrentFormParams =
        convertFormParametersToNumbersFromString(
          currentForm.data,
          flowItemService
        );

      const formsAreDifferent = findFormDifferences(
        { ...previousForm, data: convertedPreviousFormParams },
        { ...currentForm, data: convertedCurrentFormParams },
        undefined,
        BeaconCuttingData.RECOMMENDATION_CONFIG_INPUTS
      );

      const needsInitialRecommendation =
        !hasInitialRecommendation.current && isNil(flow.recommendation);

      if (!formsAreDifferent && !needsInitialRecommendation) {
        recommendationInFlight.current = false;
        return;
      }

      if (!hasInitialRecommendation.current) {
        hasInitialRecommendation.current = true;
      }

      const actualForm: FlowItemForm = currentForm.data;

      setLoadingRecommendation(true);
      setRecommendationFailed(false);

      const recommendationInfo =
        BeaconCuttingData.RECOMMENDED_PARAM_OUTPUTS.reduce<
          ParametersRecommendationInfo<BeaconCuttingData.FlowItemParameterRef>
        >((infos, paramRef) => {
          infos[paramRef] = { loading: true };

          return infos;
        }, {});

      setParametersRecommendationInfo(recommendationInfo);

      try {
        setNoRecommendationsExist(false);

        const cutRecommendation = await getCutRecommendationForForm(
          actualForm,
          currentForm.tags
        );

        if (cutRecommendation) {
          const hasRecommendations =
            cutRecommendation.recommendation.parameters.length > 0;
          let paramsToUpdate = cutRecommendation.recommendation.parameters;

          // if no recommendation is given, we need to create an "empty" recommendation to clear any existing settings
          if (!hasRecommendations) {
            paramsToUpdate = BeaconCuttingData.RECOMMENDED_PARAM_OUTPUTS.reduce<
              CutParameterRecommendation[]
            >((params, ref) => {
              params.push({
                ref,
                value: undefined,
                baseValue: undefined,
                maxValue: undefined,
                minValue: undefined,
              });
              return params;
            }, []);

            setNoRecommendationsExist(true);
          }

          setRecommendation(cutRecommendation);

          const recommendationLabels =
            createParamSubtitlesForSettingsRecommendations(paramsToUpdate);

          setParametersRecommendationInfo(recommendationLabels);
          setFormParamValues(paramsToUpdate, setFieldValue, 'data');

          if (onRecommendSettings) {
            // despite potentially not getting recommended params,
            // we still pass the recommendation and allow the user
            // to decide what to do
            onRecommendSettings(cutRecommendation.id);
          }
        }
      } catch (error) {
        logger.error('failed to recommend parameters for form', {
          error,
          form: actualForm,
          tags: currentForm.tags,
        });
        setParametersRecommendationInfo(undefined);
        setRecommendationFailed(true);
      } finally {
        trackScratchpadRecommendationCreated();
        setLoadingRecommendation(false);
        recommendationInFlight.current = false;
      }
    },
    [
      flow.recommendation,
      flowItemService,
      getCutRecommendationForForm,
      onRecommendSettings,
    ]
  );

  const recommendParametersDebounced = useMemo(() => {
    return debounce(recommendParameters, 1000);
  }, [recommendParameters]);

  useEffect(() => {
    updateSchema(formik.values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSchema, JSON.stringify(formik.values)]);

  useEffect(() => {
    if (
      !isMounted() ||
      !isScratchpadRecommendationEnabled ||
      preventRecommendation
    ) {
      return;
    }

    recommendParametersDebounced(
      formik.values,
      previousForm,
      formik.setFieldValue,
      formik.validateForm
    );

    return () => {
      recommendParametersDebounced.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.setFieldValue,
    formik.validateForm,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(formik.values),
    recommendParametersDebounced,
    previousForm,
    isMounted,
    preventRecommendation,
    isScratchpadRecommendationEnabled,
  ]);

  const parameterExtensions = useMemo(() => {
    return {
      widgetExtensions: extensions,
    };
  }, [extensions]);

  return (
    <PaywallWrapper planEntitlementKey={PlanEntitlementKey.AI_RECOMMENDATIONS}>
      <>
        <div style={{ padding: '0px 16px' }}>
          <IonText>
            <p>
              {getLocalizedText(
                'cutting:scratchpad_settings_recommendation_details'
              )}
            </p>
          </IonText>
        </div>
        {noRecommendationsExist ? (
          <NoRecommendationsInfo />
        ) : (
          <>
            {recommendation && (
              <EdgeStartInfo recommendation={recommendation} />
            )}
            <ParametersList
              extensions={parameterExtensions}
              parameters={params}
              parametersRecommendationInfo={parametersRecommendationInfo}
              system={system}
              readOnly={readOnly}
              formPrefix={parameterListPrefix}
            />
          </>
        )}
        <SaveStatusToast
          loading={isLoadingRecommendation}
          error={recommendationFailed}
          defaultErrorMessageKey="cutting:scratchpad_settings_recommendation_failed"
          showOnly="error"
        />
      </>
    </PaywallWrapper>
  );
};

function containsValidErrors(
  errors: FormikErrors<ApplicationDefaultParameterSetLocalFormikFormValues>
): boolean {
  const acceptableParams: string[] = [
    ...BeaconCuttingData.RECOMMENDED_PARAM_OUTPUTS,
  ];
  const erroredParams = Object.keys(errors?.data?.parameters ?? {});
  const validErrors = erroredParams.filter(
    (param) => !acceptableParams.includes(param)
  );

  return validErrors.length > 0;
}

interface EdgeStartInfoProps {
  recommendation: SingleCutRecommendation;
}

const EdgeStartInfo = (props: EdgeStartInfoProps): ReactElement => {
  const { recommendation } = props;

  const display = getParameterValue<boolean>(
    {
      ref: BeaconCuttingData.FlowItemRef.CUT,
      parameters: recommendation.recommendation.parameters,
    } as FlowItem,
    BeaconCuttingData.FlowItemParameterRef.EDGE_START
  );

  return display ? (
    <IonItem lines="full" className="transparent-background">
      <IntercomHelp
        articleId={IntercomArticle.APP_CUTTING_EDGE_START}
        description={getLocalizedText('cutting:edge_start_required')}
      />
    </IonItem>
  ) : (
    <></>
  );
};

const NoRecommendationsInfo = (): ReactElement => {
  return (
    <IonItem lines="none" className="transparent-background ion-text-center">
      <IonGrid>
        <IonText color="primary">
          {getLocalizedText(
            'cutting:scratchpad_settings_recommendation_not_found'
          )}
        </IonText>
      </IonGrid>
    </IonItem>
  );
};

export { AssistedCutProcessSettingsForm };
